import React from 'react'
import { Link } from 'react-router-dom'
import { Brain, Users, Zap, Target, Film, Search, ArrowRight, Check } from 'lucide-react'

const SERVICES = [
  {
    icon: Brain,
    title: 'Branding & Identidade Visual',
    tagline: 'Sua marca com personalidade, propósito e presença.',
    desc: 'Desenvolvemos identidades visuais únicas que capturam a essência da sua marca de forma humanizada e autêntica. Desde o diagnóstico de posicionamento até a entrega do manual de identidade visual, construímos marcas que são reconhecidas, desejadas e lembradas.',
    items: [
      'Diagnóstico de posicionamento de marca',
      'Naming e arquitetura de marca',
      'Identidade visual completa (logo, cores, tipografia)',
      'Manual de marca e guia de aplicação',
      'Tom de voz e identidade verbal',
    ],
    cta: 'Quero uma marca forte',
    href: '#branding',
  },
  {
    icon: Users,
    title: 'Mídias Sociais',
    tagline: 'Presença estratégica que constrói comunidade e converte.',
    desc: 'Gerenciamos suas redes sociais de maneira estratégica, potencializando o engajamento com seus seguidores e transformando público em relacionamento real. Cada post é pensado a partir do comportamento do seu público e dos objetivos do seu negócio.',
    items: [
      'Planejamento editorial mensal',
      'Criação de conteúdo (design e copy)',
      'Gestão de Instagram e Facebook',
      'Relatório de performance mensal',
      'Estratégia de crescimento orgânico',
    ],
    cta: 'Transforme suas redes sociais',
    href: '#midias-sociais',
  },
  {
    icon: Target,
    title: 'Tráfego Pago',
    tagline: 'Campanhas que atraem, convertem e fidelizam com precisão.',
    desc: 'Atraímos, convertemos e fidelizamos clientes através de campanhas orientadas a dados e estratégias personalizadas. Gerenciamos Google Ads, Meta Ads e outras plataformas com foco em ROI mensurável e crescimento escalável.',
    items: [
      'Estratégia de mídia paga',
      'Criação e gestão de campanhas Google Ads e Meta Ads',
      'Otimização contínua de campanhas',
      'Relatórios de performance e ROI',
      'Configuração e gestão de CRM para organizar os leads gerados',
    ],
    cta: 'Quero mais leads qualificados',
    href: '#trafego-pago',
  },
  {
    icon: Zap,
    title: 'Sites & E-commerces',
    tagline: 'Experiências digitais que guiam até a decisão de compra.',
    desc: 'Criamos websites responsivos e e-commerces otimizados, proporcionando uma experiência de navegação segura e intuitiva. Cada projeto é desenvolvido com foco em conversão, velocidade e posicionamento nos mecanismos de busca.',
    items: [
      'Sites institucionais responsivos',
      'Landing pages de alta conversão',
      'E-commerces completos',
      'Otimização SEO on-page',
      'Integração com CRM e ferramentas de marketing',
    ],
    cta: 'Quero um site que converte',
    href: '#sites',
  },
  {
    icon: Film,
    title: 'Produção Audiovisual',
    tagline: 'Conteúdo visual que impacta e permanece na memória.',
    desc: 'Oferecemos solução de produção audiovisual de alta qualidade, transformando ideias em projetos marcantes. Desde vídeos institucionais até reels e conteúdo para redes sociais, com foco em atenção, emoção e conversão.',
    items: [
      'Roteirização Estratégica',
      'Vídeos institucionais e comerciais',
      'Conteúdo para redes sociais (Reels, Stories)',
      'Sessões fotográficas de produto e marca',
      'Edição e pós-produção',
    ],
    cta: 'Quero produção audiovisual',
    href: '#producao-audiovisual',
  },
  {
    icon: Search,
    title: 'SEO Estratégico',
    tagline: 'Presença e busca orgânica no Google e nas redes sociais.',
    desc: 'Trabalhamos SEO de forma completa, unindo estrutura técnica, velocidade, indexação, palavras-chave e conteúdo estratégico para ampliar a visibilidade no Google e nas redes sociais, fortalecendo o crescimento orgânico no longo prazo. ',
    items: [
      'Otimização de conteúdo para SEO',
      'Auditoria técnica completa do site',
      'Otimização de velocidade e Core Web Vitals',
      'Pesquisa e estratégia de palavras-chave',
      'Otimização de busca dentro das redes sociais (bio, legendas, hashtags)',
    ],
    cta: 'Quero aparecer no Google',
    href: '#seo',
  },
]

const WHATSAPP_BASE = 'https://api.whatsapp.com/send?phone=554396659096&text='

function ServiceCard({ service }) {
  const Icon = service.icon

  return (
    <div id={service.href.slice(1)} className='card-dorac scroll-mt-24'>
      <div className='mb-4 flex items-start gap-4'>
        <div className='flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-dorac-purple/10'>
          <Icon className='h-6 w-6 text-dorac-purple' />
        </div>
        <div className='flex-1'>
          <h3 className='text-lg font-bold text-dorac-ink'>{service.title}</h3>
          <p className='mt-1 text-sm text-dorac-purple'>{service.tagline}</p>
        </div>
      </div>

      <p className='mb-6 text-sm leading-relaxed text-dorac-ink/70'>{service.desc}</p>
      <span className='mb-3 block text-xs font-bold uppercase tracking-widest text-dorac-ink/40'>O que entregamos</span>
      <ul className='mb-6 space-y-2'>
        {service.items.map((item) => (
          <li key={item} className='flex items-start gap-2 text-sm text-dorac-ink/70'>
            <Check className='mt-0.5 h-4 w-4 flex-shrink-0 text-dorac-purple' />
            {item}
          </li>
        ))}
      </ul>
      <a
        href={`${WHATSAPP_BASE}${encodeURIComponent(`Olá, vim pelo site e ${service.cta.toLowerCase()}!`)}`}
        target='_blank'
        rel='noopener noreferrer'
        className='btn-dorac gap-2 px-6 py-3 text-sm'
      >
        {service.cta}
        <ArrowRight className='h-4 w-4' />
      </a>
    </div>
  )
}

function OurServices() {
  return (
    <>
      <section className='dorac-grid-bg pb-20 pt-32'>
        <div className='container mx-auto max-w-3xl px-4 text-center'>
          <span className='mb-3 block text-xs font-bold uppercase tracking-widest text-dorac-purple'>
            Nossas soluções
          </span>
          <h1 className='section-title mb-6'>
            Todas as frentes de comunicação
            <br />
            <span className='text-dorac-purple'>que o seu negócio precisa</span>
          </h1>
          <p className='text-base leading-relaxed text-dorac-ink/60 md:text-lg'>
            Em um fluxo integrado, estratégico e orientado para resultados. Não vendemos execução isolada, desenhamos
            coerência entre posicionamento, conteúdo, performance e experiência digital.
          </p>
        </div>
      </section>

      <section className='bg-white py-24'>
        <div className='container mx-auto px-4'>
          <div className='mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2'>
            {SERVICES.map((service) => (
              <ServiceCard key={service.title} service={service} />
            ))}
          </div>
        </div>
      </section>

      <section className='dorac-grid-bg py-24'>
        <div className='container mx-auto max-w-2xl px-4 text-center'>
          <h2 className='section-title mb-6'>Não sabe por onde começar?</h2>
          <p className='section-subtitle mx-auto mb-8'>
            Agende um diagnóstico estratégico gratuito e descubra qual solução faz mais sentido para o seu momento de
            negócio.
          </p>
          <Link to='/contato' className='btn-dorac px-8 py-4'>
            Solicitar diagnóstico gratuito
          </Link>
        </div>
      </section>
    </>
  )
}

export default OurServices
